import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { COPILOT_ELIGIBILITY, TONE, getToneFromScore } from '../../data/constants'
import { getAddOnCoveragePct, getEligibleSeats, getEligibleUsersWithoutSeat } from '../../data/reportModel'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './DeploymentReadinessSection.module.scss'

function UserCell({ row }) {
  return (
    <>
      <div className="text-body fw-bold">{row.displayName || row.userPrincipalName}</div>
      <div className={styles.cellSub}>{row.mail || row.department || row.userPrincipalName}</div>
    </>
  )
}

UserCell.propTypes = {
  row: PropTypes.shape({
    displayName: PropTypes.string,
    userPrincipalName: PropTypes.string,
    mail: PropTypes.string,
    department: PropTypes.string,
  }).isRequired,
}

export default function CopilotAddOnAssigned({ summary, users, userCounts = {} }) {
  const addOnPct = getAddOnCoveragePct(summary)
  const eligibleSeats = getEligibleSeats(summary)
  const withoutSeat = getEligibleUsersWithoutSeat(summary)

  const eligibleWithoutAddOn = useMemo(
    () =>
      users
        .filter((user) => user.eligibility === COPILOT_ELIGIBILITY.WITHOUT_ADDON)
        .map((user) => ({ ...user, key: user.entityId, baseLicense: user.currentLicenseName })),
    [users],
  )
  const eligibleWithoutAddOnCount = userCounts.eligibleWithoutAddOn ?? withoutSeat
  const footer = eligibleWithoutAddOnCount > eligibleWithoutAddOn.length && eligibleWithoutAddOn.length > 0 && (
    <FormattedMessage
      id="AI_RISK_REPORT.DEPLOYMENT.ADDON.TABLE_FOOTER"
      values={{ shown: eligibleWithoutAddOn.length, total: eligibleWithoutAddOnCount }}
    />
  )

  const columns = [
    {
      key: 'user',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ADDON.COL_USER" />,
      render: (row) => <UserCell row={row} />,
      sortable: true,
      sortValue: (row) => (row.displayName || row.userPrincipalName || '').toLowerCase(),
    },
    {
      key: 'baseLicense',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ADDON.COL_BASE_LICENSE" />,
      render: (row) => row.baseLicense || '-',
      sortable: true,
      sortValue: (row) => (row.baseLicense || '').toLowerCase(),
    },
    {
      key: 'addon',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ADDON.COL_ADDON" />,
      render: () => (
        <Chip tone={TONE.WARN}>
          <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ADDON.ADDON_NOT_ASSIGNED" />
        </Chip>
      ),
    },
  ]

  let emptyState
  if (addOnPct === null) {
    emptyState = <EmptyState messageId="AI_RISK_REPORT.DEPLOYMENT.ADDON.NOT_ASSESSED" />
  } else if (eligibleWithoutAddOnCount > 0) {
    emptyState = (
      <EmptyState
        messageId="AI_RISK_REPORT.DEPLOYMENT.ADDON.NO_USER_DATA"
        values={{ count: eligibleWithoutAddOnCount }}
      />
    )
  } else {
    emptyState = (
      <EmptyState
        tone={TONE.OK}
        titleId="AI_RISK_REPORT.DEPLOYMENT.ADDON.ALL_ASSIGNED_TITLE"
        messageId="AI_RISK_REPORT.DEPLOYMENT.ADDON.ALL_ASSIGNED"
      />
    )
  }

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DEPLOYMENT.ADDON.TITLE"
      subtitleId="AI_RISK_REPORT.DEPLOYMENT.ADDON.SUBTITLE"
      score={addOnPct}
      tone={getToneFromScore(addOnPct)}
    >
      <SubArea
        titleId="AI_RISK_REPORT.DEPLOYMENT.ADDON.TABLE_TITLE"
        detailId={eligibleWithoutAddOnCount > 0 ? 'AI_RISK_REPORT.DEPLOYMENT.ADDON.TABLE_COUNT' : undefined}
        detailValues={{
          count: eligibleWithoutAddOnCount,
          total: eligibleSeats || eligibleWithoutAddOnCount,
        }}
        descriptionId="AI_RISK_REPORT.DEPLOYMENT.ADDON.TABLE_DESC"
      >
        <ReportTable columns={columns} rows={eligibleWithoutAddOn} empty={emptyState} footer={footer} />
      </SubArea>
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  licensing: PropTypes.shape({
    eligibleBase: PropTypes.shape({ skuCount: PropTypes.number }),
  }),
})

CopilotAddOnAssigned.propTypes = {
  summary: summaryPropType.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userCounts: PropTypes.shape({ eligibleWithoutAddOn: PropTypes.number }),
}
